import React from "react";
import { useState, useMemo } from "react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import {
  CheckCircle2, XCircle, ChevronDown, ChevronUp, ChevronRight, Layers,
  FilePlus, FileText, Ban, Calendar, ArrowRight, Download,
  Award, Banknote, Euro, FolderPlus, CalendarPlus, Hammer, TrendingUp,
  User, Phone, Mail, MapPin, Home, Building, Ruler
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  computeSteps, renderTimeline, getSecondProgramSteps,
  TIMELINE_174_STEPS, MPR_TIMELINE_STEPS, REGIE_MARGIN_THRESHOLD,
  GESTES_ISO_LABELS, COUT_UNITAIRE, calc174, calcMPR,
  calcAutoQuantites174, buildCashFlowEvents, PRECARITE_COLORS,
  CHARGES_MPR_DEFAUT, DOCS_PROFIL as SHARED_DOCS_PROFIL, DOCS_PROG as SHARED_DOCS_PROG
} from "@/utils/simulateurUtils";
import { EligCard } from "./EligCard";
import { getUserRole, getUserCommissions, getUserPaymentDelays } from "../Login";
import { RdvAdminModal } from "../../components/RdvAdminModal";

const formatCurrency = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v || 0);

export function ResultsStep({ recommendation, selectedDetail, setSelectedDetail, elig174, eligMPR, eligCumule, results174, best174, active174, allMPRResults, bestMPR, activeMPR, active174Cumule, activeMPRCumule, autoQtes, couts, valorisation, setValorisation, regieMode, setRegieMode, regies, selected174Idx, setSelected174Idx, selectedMPRType, setSelectedMPRType, zone, surface, precarite, isJaune, periode, periodeMPRAvant, navigate, isRegieUser, profil, typeBatiment, chauffage, niveaux, dpeActuel, ageLogement, nbPersonnes, regionFiscale, revenuFiscal, gestesDeja, multiN, multiLogement, nbLogements, surfaceMode, surfacesDetail, chauffageMode, chauffagesDetail, dpeMode, dpesDetail, ageMode, agesDetail, precariteMode, precaritesDetail, isAdminFull, isAdminRestricted, adminSaving, setAdminSaving }) {
  const activeDetail = selectedDetail || (recommendation.length > 0 ? recommendation[0].type : null);
  const isCum = activeDetail === "cumule";
  // Admin restricted: hide costs, marge, valorisation, flux trésorerie, export
  const showFinancials = isAdminFull && !isAdminRestricted;
  const showExport = isAdminFull && !isAdminRestricted;

  const display174 = isCum
    ? results174.filter(r => !eligCumule.maxSauts || r.scenario.sauts <= eligCumule.maxSauts).map(r => ({ scenario: r.scenario, result: calc174(surface, r.scenario, valorisation, couts, autoQtes, regies.r174Cum, isJaune) }))
    : results174;
  const displayMPR = isCum
    ? ["PAC", "PAC/SSC", "PAC + CESI"].map(type => ({ type, result: calcMPR(zone, surface, precarite, type, periodeMPRAvant, regies.rMPRCum) }))
    : allMPRResults;
  const displayBestMPR = displayMPR.length > 0 ? displayMPR.reduce((b, r) => r.result.marge > b.result.marge ? r : b, displayMPR[0]) : null;

  // Hooks for regie save (must be unconditional)
  const autoSaveRef = React.useRef(false);
  const [showSaveModal, setShowSaveModal] = React.useState(false);
  const [showDossierModal, setShowDossierModal] = React.useState(false);
  const [showRdvModal, setShowRdvModal] = React.useState(false);
  const [dossierProgramme, setDossierProgramme] = React.useState(null);
  const [benefForm, setBenefForm] = React.useState({ nom: "", telephone: "", email: "", adresse: "" });
  const [dossierForm, setDossierForm] = React.useState({ nom: "", telephone: "", email: "", adresse: "" });
  const [saving, setSaving] = React.useState(false);
  const [savedId, setSavedId] = React.useState(null);
  const [activeRegieDetail, setActiveRegieDetail] = React.useState(null);
  const [showPerLogement, setShowPerLogement] = React.useState(false);
  const [showBenefPopup, setShowBenefPopup] = React.useState(false);
  const [selectedProgramme, setSelectedProgramme] = React.useState(null);
  const [benefInfo, setBenefInfo] = React.useState({
    client_name: "", client_email: "", client_phone: "",
    adresse_logement: "", code_postal: "", zone: zone || "",
    profil_client: "", notes: "",
    organisme_bailleur: "", nom_representant: "", tel_representant: "",
    fixe_representant: "", email_representant: "", adresse_bailleur: "",
    nom_residence: "", adresse_residence: "", coordonnees_gardien: "",
  });

  // Open beneficiary popup before creating dossier
  const openBenefPopup = (programme) => {
    setSelectedProgramme(programme);
    setBenefInfo(prev => ({
      ...prev,
      zone: zone || "",
    }));
    setShowBenefPopup(true);
  };

  // Compute per-logement results for detail mode
  const perLogementResults = React.useMemo(() => {
    if (!multiLogement || !active174 || multiN <= 1) return [];
    const scenario = active174.scenario;
    const isCumSelected = selectedDetail === "cumule" && eligCumule?.order;
    const results = [];
    for (let i = 0; i < nbLogements; i++) {
      const logSurface = surfaceMode === "detail" && surfacesDetail[i] ? surfacesDetail[i] : surface;
      const logChauffage = chauffageMode === "detail" && chauffagesDetail[i] ? chauffagesDetail[i] : chauffage;
      const logAutoQtes = calcAutoQuantites174(logSurface, typeBatiment, niveaux, gestesDeja, logChauffage);
      const logResult = calc174(logSurface, scenario, valorisation, couts, logAutoQtes, isCumSelected ? regies.r174Cum : regies.r174, isJaune);
      results.push({ index: i, surface: logSurface, chauffage: logChauffage, autoQtes: logAutoQtes, result: logResult });
    }
    return results;
  }, [multiLogement, active174, multiN, nbLogements, surfaceMode, surfacesDetail, surface, chauffageMode, chauffagesDetail, chauffage, typeBatiment, niveaux, gestesDeja, valorisation, couts, regies, isJaune, selectedDetail, eligCumule]);

  // Compute per-logement MPR results for detail mode
  const perLogementMPRResults = React.useMemo(() => {
    if (!multiLogement || multiN <= 1) return [];
    if (!eligMPR.eligible && !(eligCumule.eligible && eligCumule.order)) return [];
    const isCumSelected = selectedDetail === "cumule" && eligCumule?.order;
    const mprType = activeMPR?.type || "PAC";
    const results = [];
    for (let i = 0; i < nbLogements; i++) {
      const logSurface = surfaceMode === "detail" && surfacesDetail[i] ? surfacesDetail[i] : surface;
      const logResult = calcMPR(zone, logSurface, precarite, mprType, periodeMPRAvant, isCumSelected ? regies.rMPRCum : regies.rMPR);
      results.push({ index: i, surface: logSurface, type: mprType, result: logResult });
    }
    return results;
  }, [multiLogement, multiN, nbLogements, surfaceMode, surfacesDetail, surface, zone, precarite, activeMPR, periodeMPRAvant, regies, eligMPR, eligCumule, selectedDetail]);

  // Auto-save for regie user (unconditional hook)
  React.useEffect(() => {
    if (!isRegieUser || autoSaveRef.current) return;
    autoSaveRef.current = true;
    const API = `${typeof window !== "undefined" ? window.location.origin : ""}/api`;
    const payload = {
      source: localStorage.getItem("label_energie_user") || "regie",
      profil, periode, typeBatiment, surface, zone, chauffage, niveaux,
      dpeActuel, ageLogement, nbPersonnes, regionFiscale, revenuFiscal,
      precarite, gestesDeja: gestesDeja || [],
      elig174: { eligible: elig174.eligible },
      eligMPR: { eligible: eligMPR.eligible },
      eligCumule: { eligible: eligCumule.eligible, order: eligCumule.order },
      active174Result: active174?.result || null,
      activeMPRResult: activeMPR?.result || null,
      activeMPRType: activeMPR?.type || null,
      active174CumResult: active174Cumule?.result || null,
      activeMPRCumResult: activeMPRCumule?.result || null,
      regies,
      valorisation, regieMode,
      allMPRResults: allMPRResults.map(r => ({ type: r.type, result: r.result })),
      results174: results174.map(r => ({ scenario: { label: r.scenario.label, sauts: r.scenario.sauts, gestes: r.scenario.gestes, equipements: r.scenario.equipements }, result: r.result })),
      couts: Object.fromEntries(Object.entries(couts).map(([k, v]) => [k, { materiel: v.materiel, pose: v.pose }])),
      autoQtes,
    };
    fetch(`${API}/etudes`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) }).catch(() => {});
    // eslint-disable-next-line
  }, [isRegieUser]);

  // ═══ ADMIN: Create pre-filled dossier from simulator ═══
  const handleAdminCreateDossier = async (programme) => {
    const API = `${typeof window !== "undefined" ? window.location.origin : ""}/api`;
    setAdminSaving(true);
    try {
      // Build travaux_realises from active scenario
      const travauxRealises = {};
      let activeScenario = active174;
      if (programme === "cumule" && active174Cumule) activeScenario = active174Cumule;
      if (programme === "mpr") activeScenario = null; // MPR doesn't use 174 scenarios

      if (activeScenario?.scenario) {
        const sc = activeScenario.scenario;
        const allGestes = [...(sc.gestes || []), ...(sc.equipements || [])];
        allGestes.forEach(g => {
          const qty = autoQtes[g] || 0;
          const cout = couts[g] || {};
          travauxRealises[g] = {
            selected: true,
            quantite: qty * (multiN || 1),
            cout_marchandise: (cout.materiel || 0) * qty * (multiN || 1),
            cout_pose: (cout.pose || 0) * qty * (multiN || 1),
          };
        });
      }

      // Build MPR travaux if MPR programme
      if ((programme === "mpr" || programme === "cumule") && activeMPR) {
        // MPR equipment types
        const mprType = activeMPR.type || "PAC";
        const mprEquipKey = mprType === "PAC/SSC" ? "pac_air_eau" : mprType === "PAC + CESI" ? "pac_air_eau" : "pac_air_eau";
        if (!travauxRealises[mprEquipKey]) {
          travauxRealises[mprEquipKey] = {
            selected: true,
            quantite: multiN || 1,
            cout_marchandise: 0,
            cout_pose: 0,
          };
        }
      }

      // Build full simulation_data object
      const simulationData = {
        profil, periode, typeBatiment, surface, zone, chauffage, niveaux,
        dpeActuel, ageLogement, nbPersonnes, regionFiscale, revenuFiscal,
        precarite, gestesDeja: gestesDeja || [],
        multiLogement, multiN: multiN || 1, nbLogements,
        multiLogementInfo: multiLogement ? {
          surfaceMode, surfacesDetail: surfacesDetail || [],
          chauffageMode, chauffagesDetail: chauffagesDetail || [],
          dpeMode, dpesDetail: dpesDetail || [],
          ageMode, agesDetail: agesDetail || [],
          precariteMode, precaritesDetail: precaritesDetail || [],
        } : null,
        elig174: { eligible: elig174.eligible, reason: elig174.reason },
        eligMPR: { eligible: eligMPR.eligible, reason: eligMPR.reason },
        eligCumule: { eligible: eligCumule.eligible, order: eligCumule.order },
        active174Result: active174?.result || null,
        activeMPRResult: activeMPR?.result || null,
        activeMPRType: activeMPR?.type || null,
        active174CumResult: active174Cumule?.result || null,
        activeMPRCumResult: activeMPRCumule?.result || null,
        results174: results174.map(r => ({ scenario: { label: r.scenario.label, sauts: r.scenario.sauts, gestes: r.scenario.gestes, equipements: r.scenario.equipements }, result: r.result })),
        allMPRResults: allMPRResults.map(r => ({ type: r.type, result: r.result })),
        valorisation, regieMode,
        couts: Object.fromEntries(Object.entries(couts).map(([k, v]) => [k, { materiel: v.materiel, pose: v.pose }])),
        autoQtes,
        regies,
        recommendation: recommendation.map(r => ({ type: r.type, label: r.label })),
        programme_selectionne: programme,
        isJaune,
        periodeMPRAvant,
      };

      // Determine client profile mapping
      const profileMap = { bailleur_sci: "bailleur", bailleur_rs: "bailleur", "propriétaire_occupant": "proprietaire_occupant", proprietaire_bailleur: "bailleur", sci: "sci" };
      const clientProfile = benefInfo.profil_client || profileMap[profil] || "proprietaire_occupant";

      // Calculate financial data from simulation (use per-logement sums when detail surfaces)
      let margePrevue = 0;
      let totalAides = 0;
      let mprCalcAmount = 0;
      let ceeCalcAmount = 0;
      let marge174 = 0;
      let margeMpr = 0;

      const usePerLogement = multiLogement && surfaceMode === "detail" && multiN > 1;

      if (programme === "174" && active174) {
        if (usePerLogement && perLogementResults.length > 0) {
          margePrevue = perLogementResults.reduce((s, r) => s + r.result.marge, 0);
          ceeCalcAmount = perLogementResults.reduce((s, r) => s + r.result.cee, 0);
        } else {
          margePrevue = (active174.result.marge || 0) * (multiN || 1);
          ceeCalcAmount = (active174.result.cee || 0) * (multiN || 1);
        }
        totalAides = ceeCalcAmount;
        marge174 = margePrevue;
      } else if (programme === "mpr" && activeMPR) {
        if (usePerLogement && perLogementMPRResults.length > 0) {
          margePrevue = perLogementMPRResults.reduce((s, r) => s + r.result.marge, 0);
          mprCalcAmount = perLogementMPRResults.reduce((s, r) => s + r.result.mpr, 0);
          ceeCalcAmount = perLogementMPRResults.reduce((s, r) => s + r.result.cee, 0);
        } else {
          margePrevue = (activeMPR.result.marge || 0) * (multiN || 1);
          mprCalcAmount = (activeMPR.result.aideMPR || activeMPR.result.mpr || 0) * (multiN || 1);
          ceeCalcAmount = (activeMPR.result.cee || 0) * (multiN || 1);
        }
        totalAides = mprCalcAmount + ceeCalcAmount;
        margeMpr = margePrevue;
      } else if (programme === "cumule" && active174Cumule && activeMPRCumule) {
        if (usePerLogement && perLogementResults.length > 0 && perLogementMPRResults.length > 0) {
          marge174 = perLogementResults.reduce((s, r) => s + r.result.marge, 0);
          margeMpr = perLogementMPRResults.reduce((s, r) => s + r.result.marge, 0);
          ceeCalcAmount = perLogementResults.reduce((s, r) => s + r.result.cee, 0) + perLogementMPRResults.reduce((s, r) => s + r.result.cee, 0);
          mprCalcAmount = perLogementMPRResults.reduce((s, r) => s + r.result.mpr, 0);
        } else {
          marge174 = (active174Cumule.result.marge || 0) * (multiN || 1);
          margeMpr = (activeMPRCumule.result.marge || 0) * (multiN || 1);
          mprCalcAmount = (activeMPRCumule.result.aideMPR || activeMPRCumule.result.mpr || 0) * (multiN || 1);
          ceeCalcAmount = ((active174Cumule.result.cee || 0) + (activeMPRCumule.result.cee || 0)) * (multiN || 1);
        }
        margePrevue = marge174 + margeMpr;
        totalAides = mprCalcAmount + ceeCalcAmount;
      }

      // Build dossier payload with beneficiary info
      const dossierPayload = {
        client_name: benefInfo.client_name || localStorage.getItem("label_energie_user") || "Admin",
        client_email: benefInfo.client_email || "",
        client_phone: benefInfo.client_phone || "",
        profile: clientProfile,
        programme: programme === "cumule" ? "cumule" : programme === "mpr" ? "MPR" : "174",
        zone: benefInfo.zone || zone,
        metrage: parseFloat(surface) || 0,
        precarite: precarite,
        code_postal: benefInfo.code_postal || "",
        adresse_logement: benefInfo.adresse_logement || "",
        notes: benefInfo.notes || "",
        nombre_logements: multiN || 1,
        type_logements: typeBatiment,
        montant_devis_174: active174?.result?.coutTotal || 0,
        scenario_classes: active174?.scenario?.sauts || 2,
        travaux_realises: travauxRealises,
        simulation_data: simulationData,
        statut_dossier: "nouveau",
        assigned_to: localStorage.getItem("label_energie_user") || "",
        // Beneficiary specific fields
        organisme_bailleur: benefInfo.organisme_bailleur || "",
        nom_representant: benefInfo.nom_representant || "",
        tel_representant: benefInfo.tel_representant || "",
        fixe_representant: benefInfo.fixe_representant || "",
        email_representant: benefInfo.email_representant || "",
        adresse_bailleur: benefInfo.adresse_bailleur || "",
        nom_residence: benefInfo.nom_residence || "",
        adresse_residence: benefInfo.adresse_residence || "",
        coordonnees_gardien: benefInfo.coordonnees_gardien || "",
        // Financial pre-fill via sim_ fields
        sim_marge_prevue: margePrevue,
        sim_total_aides: totalAides,
        sim_mpr_calcule: mprCalcAmount,
        sim_cee_calcule: ceeCalcAmount,
        sim_marge_174: marge174,
        sim_marge_mpr: margeMpr,
      };

      const res = await fetch(`${API}/dossiers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dossierPayload),
      });
      const data = await res.json();
      setShowBenefPopup(false);
      toast.success("Dossier créé avec succès !");
      navigate(`/dossiers/${data.id}`);
    } catch (err) {
      toast.error("Erreur lors de la création du dossier");
    }
    setAdminSaving(false);
  };

  // ═══ HELPER: Compute timelines (shared between normal & regie) ═══
  const computeAllTimelines = (detail) => {
    const today = new Date();
    const isCumView = detail === "cumule" && eligCumule?.order;
    let timelines = [];

    if (isCumView) {
      const firstRaw = eligCumule.order[0] === "MPR" ? MPR_TIMELINE_STEPS : TIMELINE_174_STEPS;
      const secondRaw = eligCumule.order[0] === "MPR" ? TIMELINE_174_STEPS : MPR_TIMELINE_STEPS;
      const firstSteps = computeSteps(firstRaw, today);
      const firstEnd = firstSteps[firstSteps.length - 1].day;
      const secondFiltered = getSecondProgramSteps(secondRaw);
      const secondStart = new Date(today);
      secondStart.setDate(secondStart.getDate() + firstEnd);
      const secondSteps = computeSteps(secondFiltered, secondStart);
      secondSteps.forEach(s => s.day += firstEnd);
      timelines = [
        { label: eligCumule.order[0] === "MPR" ? "Programme MPR" : "Programme 174", steps: firstSteps, color: eligCumule.order[0] === "MPR" ? "blue" : "emerald" },
        { label: eligCumule.order[0] === "MPR" ? "Programme 174" : "Programme MPR", steps: secondSteps, color: eligCumule.order[0] === "MPR" ? "emerald" : "blue" }
      ];
    } else if (detail === "174" && elig174.eligible) {
      timelines = [{ label: "Programme 174", steps: computeSteps(TIMELINE_174_STEPS, today), color: "emerald" }];
    } else if (detail === "mpr" && eligMPR.eligible) {
      timelines = [{ label: "Programme MPR", steps: computeSteps(MPR_TIMELINE_STEPS, today), color: "blue" }];
    }

    if (timelines.length === 0) return { timelines: [], cashEvents: [], totalDays: 0 };
    const allSteps = timelines.flatMap(t => t.steps);
    const totalDays = Math.max(...allSteps.map(s => s.day));

    let cashEvents = [];
    if (isCumView && eligCumule.order) {
      const firstIsMPR = eligCumule.order[0] === "MPR";
      const firstEnd = timelines[0].steps[timelines[0].steps.length - 1].day;
      if (firstIsMPR) {
        cashEvents = [
          ...buildCashFlowEvents("mpr", today, 0, activeMPRCumule || activeMPR, null, regies, regieMode, true, false),
          ...buildCashFlowEvents("174", today, firstEnd, null, active174Cumule || active174, regies, regieMode, true, true)
        ];
      } else {
        cashEvents = [
          ...buildCashFlowEvents("174", today, 0, null, active174Cumule || active174, regies, regieMode, true, false),
          ...buildCashFlowEvents("mpr", today, firstEnd, activeMPRCumule || activeMPR, null, regies, regieMode, true, true)
        ];
      }
    } else if (detail === "174" && active174) {
      cashEvents = buildCashFlowEvents("174", today, 0, null, active174, regies, regieMode, false, false);
    } else if (detail === "mpr" && activeMPR) {
      cashEvents = buildCashFlowEvents("mpr", today, 0, activeMPR, null, regies, regieMode, false, false);
    }
    cashEvents.sort((a, b) => a.day - b.day);
    let balance = 0;
    cashEvents = cashEvents.map(e => {
      balance += e.amount;
      const d = new Date(today); d.setDate(d.getDate() + e.day);
      return { ...e, balance, dateStr: d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) };
    });

    return { timelines, cashEvents, totalDays };
  };

  // ═══ TIMELINE RENDERER (shared) ═══
  const renderTimeline = (timelines, totalDays, paymentAnnotations) => (
    <div className="border border-border rounded-xl bg-card p-5 space-y-4" data-testid="frise-chronologique">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <Calendar className="w-4 h-4 text-[hsl(var(--renova-green))]" /> Frise chronologique du montage
        </h3>
        <span className="text-xs font-bold text-[hsl(var(--renova-green))] bg-emerald-100 px-2.5 py-0.5 rounded-full">{totalDays} jours</span>
      </div>
      {timelines.map((tl, tIdx) => {
        const tlTotal = tl.steps[tl.steps.length - 1].day - tl.steps[0].day;
        const bgColor = tl.color === "blue" ? "bg-blue-500" : "bg-emerald-500";
        const bgColorLight = tl.color === "blue" ? "bg-blue-100" : "bg-emerald-100";
        const textColor = tl.color === "blue" ? "text-blue-700" : "text-emerald-700";
        const lineColor = tl.color === "blue" ? "bg-blue-300" : "bg-emerald-300";
        const borderColor = tl.color === "blue" ? "border-blue-500/30" : "border-emerald-500/30";
        return (
          <div key={tIdx} className="space-y-2">
            <div className="flex items-center gap-2">
              <div className={cn("w-2.5 h-2.5 rounded-full", bgColor)} />
              <span className={cn("text-[11px] font-bold uppercase tracking-wide", textColor)}>{tl.label}</span>
              <span className="text-[10px] text-muted-foreground">({tlTotal} jours)</span>
            </div>
            <div className="flex items-start flex-wrap gap-y-3">
              {tl.steps.map((step, i) => {
                const gap = i < tl.steps.length - 1 ? tl.steps[i + 1].day - step.day : 0;
                const dateStr = step.date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
                const isPaiement = step.labels.some(l => l.toLowerCase().includes("paiement"));
                const annotation = paymentAnnotations && isPaiement
                  ? paymentAnnotations.find(a => a.prog === (tl.color === "blue" ? "MPR" : "174"))
                  : null;
                return (
                  <div key={i} className="flex items-start">
                    <div className="flex flex-col items-center" style={{ minWidth: '68px' }}>
                      <span className={cn("text-[10px] font-bold mb-1", textColor)}>J{step.day}</span>
                      <div className={cn("w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white shadow z-10", bgColor)}>
                        {i + 1}
                      </div>
                      <div className={cn("mt-1.5 rounded-md px-1.5 py-1 text-center border shadow-sm", bgColorLight, borderColor)} style={{ minWidth: '64px', maxWidth: '80px' }}>
                        {step.labels.map((l, j) => (
                          <div key={j} className={cn("text-[9px] font-semibold leading-tight", textColor)}>{l}</div>
                        ))}
                        <span className="text-[8px] text-muted-foreground">{dateStr}</span>
                      </div>
                      {annotation && (
                        <div className="mt-1 bg-emerald-100 border border-emerald-500/40 rounded px-1.5 py-0.5 text-center">
                          <span className="text-[9px] font-bold text-emerald-700">{formatCurrency(annotation.amount)}</span>
                        </div>
                      )}
                    </div>
                    {gap > 0 && (
                      <div className="flex flex-col items-center justify-center mt-4 mx-0.5">
                        <div className={cn("h-[2px] rounded-full", lineColor)} style={{ width: `${Math.max(16, gap * 3)}px` }} />
                        <span className="text-[8px] font-semibold text-muted-foreground">+{gap}j</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {tIdx < timelines.length - 1 && (
              <div className="flex items-center gap-2 pl-3 py-0.5">
                <ArrowRight className="w-3.5 h-3.5 text-violet-500" />
                <span className="text-[10px] text-violet-600 font-semibold">Enchaînement vers {timelines[tIdx + 1].label}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  // ═══ REGIE VIEW ═══
  if (isRegieUser) {
    const REGIE_MARGIN_THRESHOLD = isRegieUser ? 4500 : 0;
    const API = `${typeof window !== "undefined" ? window.location.origin : ""}/api`;

    const regie174OK = elig174.eligible && active174 && active174.result.marge >= REGIE_MARGIN_THRESHOLD;
    const regie174Scenarios = regie174OK ? display174.filter(r => r.result.marge >= REGIE_MARGIN_THRESHOLD) : [];
    const regieMPROK = eligMPR.eligible && activeMPR && activeMPR.result.marge >= REGIE_MARGIN_THRESHOLD;
    const regieMPRScenarios = regieMPROK ? displayMPR.filter(r => r.result.marge >= REGIE_MARGIN_THRESHOLD) : [];
    const regieCumOK = eligCumule.eligible && active174Cumule && activeMPRCumule &&
      (active174Cumule.result.marge + activeMPRCumule.result.marge) >= REGIE_MARGIN_THRESHOLD;

    const hasDetailSurfaces = multiLogement && surfaceMode === "detail" && multiN > 1;

    // Compute régie earnings for each option (use per-logement sums when detail surfaces)
    const regieOptions = [];
    if (regie174OK) {
      const margin174 = hasDetailSurfaces && perLogementResults.length > 0
        ? perLogementResults.reduce((s, r) => s + r.result.marge, 0)
        : active174.result.marge * multiN;
      regieOptions.push({ type: "174", label: "Programme 174", earnings: regies.r174 * multiN, margin: margin174 });
    }
    if (regieMPROK) {
      const marginMPR = hasDetailSurfaces && perLogementMPRResults.length > 0
        ? perLogementMPRResults.reduce((s, r) => s + r.result.marge, 0)
        : activeMPR.result.marge * multiN;
      regieOptions.push({ type: "mpr", label: `Programme MPR (${activeMPR.type})`, earnings: regies.rMPR * multiN, margin: marginMPR });
    }
    if (regieCumOK) {
      const margin174Cum = hasDetailSurfaces && perLogementResults.length > 0
        ? perLogementResults.reduce((s, r) => s + r.result.marge, 0)
        : (active174Cumule?.result?.marge || 0) * multiN;
      const marginMPRCum = hasDetailSurfaces && perLogementMPRResults.length > 0
        ? perLogementMPRResults.reduce((s, r) => s + r.result.marge, 0)
        : (activeMPRCumule?.result?.marge || 0) * multiN;
      regieOptions.push({ type: "cumule", label: `Op. Cumulée (${eligCumule.order.join(" puis ")})`, earnings: (regies.r174Cum + regies.rMPRCum) * multiN, margin: margin174Cum + marginMPRCum });
    }
    // Sort: highest margin first (= recommended)
    regieOptions.sort((a, b) => b.margin - a.margin);

    if (regieOptions.length === 0) {
      return (
        <div className="space-y-6">
          <div className="border-2 border-red-300 rounded-xl bg-red-500/10 p-8 text-center">
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <h3 className="font-bold text-lg text-red-700" data-testid="regie-no-eligible">Aucun programme éligible</h3>
            <p className="text-red-600 text-sm mt-2">Ce dossier ne correspond aux critères d'aucun programme éligible.</p>
          </div>
        </div>
      );
    }

    const regieDetailDefault = regieOptions[0].type;
    const regieDetail = activeRegieDetail || regieDetailDefault;
    const { timelines: regieTL, totalDays: regieTotalDays } = computeAllTimelines(regieDetail);

    // Compute payment boxes for the recommended program
    const paymentBoxes = [];
    const multiMultiplier = multiN || 1; // multiply commissions by number of logements
    if (regieDetail === "cumule" && eligCumule.order) {
      const firstIsMPR = eligCumule.order[0] === "MPR";
      const firstRaw = firstIsMPR ? MPR_TIMELINE_STEPS : TIMELINE_174_STEPS;
      const secondRaw = firstIsMPR ? TIMELINE_174_STEPS : MPR_TIMELINE_STEPS;
      const firstSteps = computeSteps(firstRaw, new Date());
      const firstEnd = firstSteps[firstSteps.length - 1].day;
      const secondFiltered = getSecondProgramSteps(secondRaw);
      const secondSteps = computeSteps(secondFiltered, new Date());
      const secondPaiement = firstEnd + secondSteps[secondSteps.length - 1].day;
      if (firstIsMPR) {
        paymentBoxes.push({ prog: "MPR", label: `PAIEMENT COM REGIE ${activeMPR?.type || "MPR"}`, amount: regies.rMPRCum * multiMultiplier, day: firstEnd });
        paymentBoxes.push({ prog: "174", label: "PAIEMENT COM REGIE 174", amount: regies.r174Cum * multiMultiplier, day: secondPaiement });
      } else {
        paymentBoxes.push({ prog: "174", label: "PAIEMENT COM REGIE 174", amount: regies.r174Cum * multiMultiplier, day: firstEnd });
        paymentBoxes.push({ prog: "MPR", label: `PAIEMENT COM REGIE ${activeMPR?.type || "MPR"}`, amount: regies.rMPRCum * multiMultiplier, day: secondPaiement });
      }
    } else if (regieDetail === "174") {
      const steps = computeSteps(TIMELINE_174_STEPS, new Date());
      paymentBoxes.push({ prog: "174", label: "PAIEMENT COM REGIE 174", amount: regies.r174 * multiMultiplier, day: steps[steps.length - 1].day });
    } else if (regieDetail === "mpr") {
      const steps = computeSteps(MPR_TIMELINE_STEPS, new Date());
      paymentBoxes.push({ prog: "MPR", label: `PAIEMENT COM REGIE ${activeMPR?.type || "MPR"}`, amount: regies.rMPR * multiMultiplier, day: steps[steps.length - 1].day });
    }

    // Documents par profil
    const profilDocs = SHARED_DOCS_PROFIL[profil] || [];
    const progDocs = [];
    if (regieDetail === "cumule" || regieDetail === "174") progDocs.push(...(SHARED_DOCS_PROG["174"] || []));
    if (regieDetail === "cumule" || regieDetail === "mpr") progDocs.push(...(SHARED_DOCS_PROG["MPR"] || []));
    const allDocs = [...new Set([...profilDocs, ...progDocs])];

    // Beneficiary save modal
    const handleSaveEtude = async () => {
      const API = `${typeof window !== "undefined" ? window.location.origin : ""}/api`;
      setSaving(true);
      try {
        const payload = {
          source: localStorage.getItem("label_energie_user") || "regie",
          profil, periode, typeBatiment, surface, zone, chauffage, niveaux,
          dpeActuel, ageLogement, nbPersonnes, regionFiscale, revenuFiscal,
          precarite, gestesDeja: gestesDeja || [],
          regieOptions: regieOptions.map(o => ({ type: o.type, label: o.label, earnings: o.earnings })),
          recommendation: regieDetail,
          elig174: { eligible: elig174.eligible },
          eligMPR: { eligible: eligMPR.eligible },
          eligCumule: { eligible: eligCumule.eligible, order: eligCumule.order },
          active174Result: active174?.result || null,
          activeMPRResult: activeMPR?.result || null,
          activeMPRType: activeMPR?.type || null,
          active174CumResult: active174Cumule?.result || null,
          activeMPRCumResult: activeMPRCumule?.result || null,
          regies,
          paymentBoxes: paymentBoxes.map(b => ({ prog: b.prog, label: b.label, amount: b.amount, day: b.day })),
          valorisation, regieMode,
          allMPRResults: allMPRResults.map(r => ({ type: r.type, result: r.result })),
          results174: results174.map(r => ({ scenario: { label: r.scenario.label, sauts: r.scenario.sauts, gestes: r.scenario.gestes, equipements: r.scenario.equipements }, result: r.result })),
          couts: Object.fromEntries(Object.entries(couts).map(([k, v]) => [k, { materiel: v.materiel, pose: v.pose }])),
          autoQtes,
          beneficiaire: benefForm,
          savedByRegie: true,
        };
        const res = await fetch(`${API}/etudes`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
        const data = await res.json();
        setSavedId(data.id);
        setShowSaveModal(false);
        toast.success("Étude enregistrée avec succès !");
      } catch (err) { toast.error("Erreur lors de l'enregistrement"); }
      setSaving(false);
    };

    const handleCreateDossier = async () => {
      const API = `${typeof window !== "undefined" ? window.location.origin : ""}/api`;
      setSaving(true);
      const progToUse = dossierProgramme || regieDetail;
      try {
        // Build travaux_realises from active scenario
        const travauxRealises = {};
        const activeScenario = progToUse === "cumule" && active174Cumule ? active174Cumule : active174;
        if (activeScenario?.scenario) {
          const sc = activeScenario.scenario;
          const allGestes = [...(sc.gestes || []), ...(sc.equipements || [])];
          allGestes.forEach(g => {
            const qty = autoQtes[g] || 0;
            const cout = couts[g] || {};
            travauxRealises[g] = {
              selected: true,
              quantite: qty * (multiN || 1),
              cout_marchandise: (cout.materiel || 0) * qty * (multiN || 1),
              cout_pose: (cout.pose || 0) * qty * (multiN || 1),
            };
          });
        }

        const payload = {
          type: "dossier",
          source: localStorage.getItem("label_energie_user") || "regie",
          profil, periode, typeBatiment, surface, zone, chauffage, niveaux,
          dpeActuel, ageLogement, nbPersonnes, regionFiscale, revenuFiscal,
          precarite, gestesDeja: gestesDeja || [],
          regieOptions: regieOptions.map(o => ({ type: o.type, label: o.label, earnings: o.earnings })),
          recommendation: progToUse,
          elig174: { eligible: elig174.eligible },
          eligMPR: { eligible: eligMPR.eligible },
          eligCumule: { eligible: eligCumule.eligible, order: eligCumule.order },
          active174Result: active174?.result || null,
          activeMPRResult: activeMPR?.result || null,
          activeMPRType: activeMPR?.type || null,
          active174CumResult: active174Cumule?.result || null,
          activeMPRCumResult: activeMPRCumule?.result || null,
          regies,
          paymentBoxes: paymentBoxes.map(b => ({ prog: b.prog, label: b.label, amount: b.amount, day: b.day })),
          valorisation, regieMode,
          allMPRResults: allMPRResults.map(r => ({ type: r.type, result: r.result })),
          results174: results174.map(r => ({ scenario: { label: r.scenario.label, sauts: r.scenario.sauts, gestes: r.scenario.gestes, equipements: r.scenario.equipements }, result: r.result })),
          couts: Object.fromEntries(Object.entries(couts).map(([k, v]) => [k, { materiel: v.materiel, pose: v.pose }])),
          autoQtes,
          beneficiaire: dossierForm,
          savedByRegie: true,
          status: "rdv_admin",
          travaux_realises: travauxRealises,
          nombre_logements: multiN || 1,
          multi_logement_info: multiLogement ? {
            surfaceMode, surfacesDetail: surfacesDetail || [],
            chauffageMode, chauffagesDetail: chauffagesDetail || [],
            dpeMode, dpesDetail: dpesDetail || [],
            ageMode, agesDetail: agesDetail || [],
          } : null,
        };
        const res = await fetch(`${API}/etudes`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
        const data = await res.json();

        // Also create in the main dossiers collection so admin/administratif can see it
        try {
          await fetch(`${API}/dossiers`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              client_name: dossierForm.nom || "",
              client_email: dossierForm.email || "",
              client_phone: dossierForm.telephone || "",
              profile: profil === "bailleur_sci" ? "bailleur" : profil || "proprietaire_occupant",
              programme: progToUse === "cumule" ? "cumule" : progToUse === "174" ? "174" : "MPR",
              zone: zone || "H1",
              metrage: surface ? parseFloat(surface) : 0,
              precarite: precarite || "",
              code_postal: "",
              adresse_logement: dossierForm.adresse || "",
              notes: `Créé depuis le simulateur par ${localStorage.getItem("label_energie_user") || "regie"}`,
              statut_dossier: "rdv_admin",
              assigned_to: localStorage.getItem("label_energie_user") || "",
              created_by: localStorage.getItem("label_energie_user") || "",
              sim_marge_prevue: margePrevue,
              sim_total_aides: totalAides,
              sim_mpr_calcule: mprCalcAmount,
              sim_cee_calcule: ceeCalcAmount,
              sim_marge_174: marge174,
              sim_marge_mpr: margeMpr,
              nombre_logements: multiN || 1,
              travaux_realises: Object.entries(travauxRealises).map(([k, v]) => ({ poste: k, ...v })),
              etude_id: data.id,
              sim_regies: regies,
            }),
          });
        } catch (adminDossierErr) {
        }

        setShowDossierModal(false);
        toast.success("Dossier créé avec succès !");
        navigate(`/dossier-regie/${data.id}`);
      } catch (err) { toast.error("Erreur lors de la création"); }
      setSaving(false);
    };

    return (
      <div className="space-y-5">
        {/* Programmes éligibles */}
        <div className="border-2 border-[hsl(var(--renova-green))] rounded-xl bg-emerald-500/10 p-5" data-testid="regie-eligible-programs">
          <h2 className="font-heading font-bold text-base flex items-center gap-2 mb-4">
            <Award className="w-5 h-5 text-[hsl(var(--renova-green))]" />
            Programmes éligibles pour ce dossier
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {regieOptions.map((opt, i) => (
              <div key={opt.type}
                onClick={() => setActiveRegieDetail(opt.type)}
                className={cn("p-4 rounded-xl border-2 bg-card shadow-sm cursor-pointer transition-all hover:shadow-md",
                  opt.type === regieDetail ? "border-emerald-400 ring-2 ring-emerald-200" : "border-border hover:border-emerald-500/30"
                )} data-testid={`regie-option-${opt.type}`}>
                {opt.type === regieDetail && <p className="text-[10px] font-bold text-emerald-600 uppercase mb-1">{i === 0 ? "Recommandé" : "Sélectionné"}</p>}
                <p className="font-semibold text-sm">{opt.label}</p>
                <p className="text-lg font-extrabold text-[hsl(var(--renova-green))] mt-2">{formatCurrency(opt.earnings)}</p>
                <p className="text-[10px] text-muted-foreground">Commission régie totale</p>
              </div>
            ))}
          </div>

          {/* MPR scenarios */}
          {regieMPRScenarios.length > 0 && (
            <div className="mt-4 pt-3 border-t border-emerald-500/30">
              <p className="text-xs font-semibold text-muted-foreground mb-2">Scénarios MPR éligibles :</p>
              <div className="flex gap-2 flex-wrap">
                {regieMPRScenarios.map(r => (
                  <span key={r.type} className="px-3 py-1.5 rounded-lg bg-blue-500/20 text-blue-600 dark:text-blue-400 text-xs font-bold border border-blue-500/30">{r.type}</span>
                ))}
              </div>
            </div>
          )}

          {/* 174 scenarios with geste details */}
          {regie174Scenarios.length > 0 && (
            <div className="mt-3 pt-3 border-t border-emerald-500/30">
              <p className="text-xs font-semibold text-muted-foreground mb-2">Scénarios 174 éligibles :</p>
              <div className="space-y-2">
                {regie174Scenarios.map((r, i) => (
                  <div key={i} className="px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                    <span className="text-xs font-bold text-emerald-700">{r.scenario.label}</span>
                    <p className="text-[10px] text-emerald-600 mt-0.5">
                      {r.scenario.sauts} sauts — {r.scenario.gestes.map(g => GESTES_ISO_LABELS[g] || g).join(" + ")}
                      {r.scenario.equipements.length > 0 && ` + ${r.scenario.equipements.map(e => COUT_UNITAIRE[e]?.label || e).join(", ")}`}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Timeline */}
        {regieTL.length > 0 && renderTimeline(regieTL, regieTotalDays, paymentBoxes)}

        {/* Payment boxes */}
        {paymentBoxes.length > 0 && (
          <div className={cn("grid gap-3", paymentBoxes.length > 1 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 max-w-sm")} data-testid="regie-payment-boxes">
            {paymentBoxes.map((box, i) => (
              <div key={i} className={cn(
                "rounded-xl border-2 p-4 text-center shadow-md",
                box.prog === "174" ? "border-emerald-400 bg-emerald-500/10" : "border-blue-400 bg-blue-500/10"
              )} data-testid={`regie-payment-${box.prog}`}>
                <Banknote className={cn("w-7 h-7 mx-auto mb-1.5", box.prog === "174" ? "text-emerald-600" : "text-blue-600")} />
                <p className={cn("text-[10px] font-bold uppercase tracking-wide mb-1", box.prog === "174" ? "text-emerald-700" : "text-blue-700")}>{box.label}</p>
                <p className={cn("text-2xl font-extrabold", box.prog === "174" ? "text-emerald-700" : "text-blue-700")}>{formatCurrency(box.amount)}</p>
                <p className="text-xs font-semibold text-muted-foreground mt-1">J{box.day}</p>
              </div>
            ))}
          </div>
        )}

        {/* Documents requis */}
        {allDocs.length > 0 && (
          <div className="border border-border rounded-xl bg-card p-5" data-testid="regie-documents">
            <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
              <Layers className="w-4 h-4 text-[hsl(var(--renova-green))]" /> Documents à demander
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
              {allDocs.map((doc, i) => (
                <div key={i} className="flex items-center gap-2 text-xs py-1">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                  <span>{doc}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Save / Actions */}
        <div className="flex items-center gap-3 flex-wrap">
          {!savedId ? (
            <button onClick={() => setShowSaveModal(true)} className="px-5 py-2.5 rounded-lg bg-[hsl(var(--renova-green))] hover:bg-[hsl(var(--renova-green-hover))] text-white font-semibold text-sm shadow" data-testid="btn-save-etude">
              <FilePlus className="w-4 h-4 inline mr-2" /> Enregistrer l'étude
            </button>
          ) : (
            <div className="px-4 py-2.5 rounded-lg bg-emerald-100 border border-emerald-500/40 text-emerald-700 text-sm font-semibold">
              <CheckCircle2 className="w-4 h-4 inline mr-2" /> Étude enregistrée (#{savedId.slice(-6)})
            </div>
          )}
          <button onClick={() => setShowRdvModal(true)} className="px-5 py-2.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-semibold text-sm shadow" data-testid="btn-planifier-rdv">
            <CalendarPlus className="w-4 h-4 inline mr-2" /> Planifier un RDV Admin
          </button>
          {/* Separate dossier creation buttons per eligible program */}
          {regieCumOK && (
            <button onClick={() => { setDossierProgramme("cumule"); setShowDossierModal(true); }} className="px-5 py-2.5 rounded-lg bg-violet-500 hover:bg-violet-600 text-white font-semibold text-sm shadow" data-testid="btn-creer-dossier-cumule">
              <FolderPlus className="w-4 h-4 inline mr-2" /> Dossier Op. Cumulée
            </button>
          )}
          {regie174OK && (
            <button onClick={() => { setDossierProgramme("174"); setShowDossierModal(true); }} className="px-5 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-sm shadow" data-testid="btn-creer-dossier-174-regie">
              <FolderPlus className="w-4 h-4 inline mr-2" /> Dossier 174
            </button>
          )}
          {regieMPROK && (
            <button onClick={() => { setDossierProgramme("mpr"); setShowDossierModal(true); }} className="px-5 py-2.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-semibold text-sm shadow" data-testid="btn-creer-dossier-mpr-regie">
              <FolderPlus className="w-4 h-4 inline mr-2" /> Dossier MPR
            </button>
          )}
        </div>

        {/* Save Modal */}
        {showSaveModal && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowSaveModal(false)}>
            <div className="bg-card rounded-xl shadow-xl p-6 w-full max-w-md space-y-4" onClick={e => e.stopPropagation()} data-testid="save-modal">
              <h3 className="font-bold text-lg">Enregistrer l'étude</h3>
              <p className="text-sm text-muted-foreground">Renseignez les informations du bénéficiaire :</p>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold">Nom du bénéficiaire *</label>
                  <input type="text" value={benefForm.nom} onChange={e => setBenefForm(p => ({...p, nom: e.target.value}))} className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" placeholder="Nom complet" data-testid="input-benef-nom" />
                </div>
                <div>
                  <label className="text-xs font-semibold">Téléphone</label>
                  <input type="tel" value={benefForm.telephone} onChange={e => setBenefForm(p => ({...p, telephone: e.target.value}))} className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" placeholder="06 ..." data-testid="input-benef-tel" />
                </div>
                <div>
                  <label className="text-xs font-semibold">Email</label>
                  <input type="email" value={benefForm.email} onChange={e => setBenefForm(p => ({...p, email: e.target.value}))} className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" placeholder="email@..." data-testid="input-benef-email" />
                </div>
                <div>
                  <label className="text-xs font-semibold">Adresse</label>
                  <input type="text" value={benefForm.adresse} onChange={e => setBenefForm(p => ({...p, adresse: e.target.value}))} className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" placeholder="Adresse complète" data-testid="input-benef-adresse" />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowSaveModal(false)} className="flex-1 px-4 py-2 rounded-lg border border-border text-sm font-medium">Annuler</button>
                <button onClick={handleSaveEtude} disabled={saving || !benefForm.nom.trim()} className="flex-1 px-4 py-2 rounded-lg bg-[hsl(var(--renova-green))] hover:bg-[hsl(var(--renova-green-hover))] text-white text-sm font-semibold disabled:opacity-50" data-testid="btn-confirm-save">
                  {saving ? "Enregistrement..." : "Enregistrer"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Dossier Modal */}
        {showDossierModal && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowDossierModal(false)}>
            <div className="bg-card rounded-xl shadow-xl p-6 w-full max-w-md space-y-4" onClick={e => e.stopPropagation()} data-testid="dossier-modal">
              <h3 className="font-bold text-lg">Créer un dossier</h3>
              <p className="text-sm text-muted-foreground">Renseignez les informations du bénéficiaire :</p>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold">Nom du bénéficiaire *</label>
                  <input type="text" value={dossierForm.nom} onChange={e => setDossierForm(p => ({...p, nom: e.target.value}))} className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" placeholder="Nom complet" data-testid="input-dossier-nom" />
                </div>
                <div>
                  <label className="text-xs font-semibold">Téléphone</label>
                  <input type="tel" value={dossierForm.telephone} onChange={e => setDossierForm(p => ({...p, telephone: e.target.value}))} className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" placeholder="06 ..." data-testid="input-dossier-tel" />
                </div>
                <div>
                  <label className="text-xs font-semibold">Email</label>
                  <input type="email" value={dossierForm.email} onChange={e => setDossierForm(p => ({...p, email: e.target.value}))} className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" placeholder="email@..." data-testid="input-dossier-email" />
                </div>
                <div>
                  <label className="text-xs font-semibold">Adresse</label>
                  <input type="text" value={dossierForm.adresse} onChange={e => setDossierForm(p => ({...p, adresse: e.target.value}))} className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" placeholder="Adresse complète" data-testid="input-dossier-adresse" />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowDossierModal(false)} className="flex-1 px-4 py-2 rounded-lg border border-border text-sm font-medium">Annuler</button>
                <button onClick={handleCreateDossier} disabled={saving || !dossierForm.nom.trim()} className="flex-1 px-4 py-2 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold disabled:opacity-50" data-testid="btn-confirm-dossier">
                  {saving ? "Création..." : "Créer le dossier"}
                </button>
              </div>
            </div>
          </div>
        )}

        <RdvAdminModal
          open={showRdvModal}
          onClose={() => setShowRdvModal(false)}
          defaultValues={{ nom: benefForm.nom, telephone: benefForm.telephone, email: benefForm.email, adresse: benefForm.adresse, programme: regieDetail === "cumule" ? "Cumulée" : regieDetail === "174" ? "174" : "MPR" }}
          etudeId={savedId || null}
          simulationData={{ zone, surface, precarite }}
          onCreated={(dossierId) => {
            toast.success("RDV planifié et dossier créé !");
            if (dossierId) navigate(`/dossier-regie/${dossierId}`);
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Paramètres éditables */}
      {showFinancials && (
      <div className="border border-border rounded-lg bg-card p-6">
        <h3 className="font-semibold text-sm text-muted-foreground mb-3">Paramètres du calcul</h3>
        <div className="flex gap-4 flex-wrap items-end">
          <div>
            <Label className="text-xs">Valorisation CEE 174 (€/MWh){isJaune ? " — Jaune" : ""}</Label>
            <Input type="number" value={valorisation} onChange={e => setValorisation(parseFloat(e.target.value) || 0)} className="w-24 mt-1" data-testid="input-valorisation" />
          </div>
          <div>
            <Label className="text-xs">Mode de coût</Label>
            <div className="flex gap-1 mt-1">
              <button onClick={() => setRegieMode("regie")}
                className={cn("px-3 py-1.5 rounded-lg text-xs font-medium border-2 transition-all",
                  regieMode === "regie" ? "border-[hsl(var(--renova-green))] bg-emerald-500/10 text-emerald-700" : "border-border hover:border-emerald-500/40"
                )} data-testid="mode-regie">Régie</button>
              <button onClick={() => setRegieMode("lead")}
                className={cn("px-3 py-1.5 rounded-lg text-xs font-medium border-2 transition-all",
                  regieMode === "lead" ? "border-[hsl(var(--renova-green))] bg-emerald-500/10 text-emerald-700" : "border-border hover:border-emerald-500/40"
                )} data-testid="mode-lead">Lead</button>
            </div>
          </div>
          <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-muted/50 text-xs">
            <div>
              <span className="text-muted-foreground">174:</span> <span className="font-medium">{regies.r174}€</span>
              <span className="text-muted-foreground ml-2">MPR:</span> <span className="font-medium">{regies.rMPR}€</span>
            </div>
            {eligCumule.eligible && (
              <div className="border-l border-border pl-3">
                <span className="text-muted-foreground">Cum. 174:</span> <span className="font-medium">{regies.r174Cum}€</span>
                <span className="text-muted-foreground ml-2">Cum. MPR:</span> <span className="font-medium">{regies.rMPRCum}€</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50 text-xs">
            <span className={cn("w-3 h-3 rounded-full", PRECARITE_COLORS[precarite]?.bg)} />
            <span className="font-medium">{PRECARITE_COLORS[precarite]?.label}</span>
          </div>
        </div>
      </div>
      )}

      {/* Recommandation */}
      {recommendation.length > 0 && (
        <div className="border-2 border-[hsl(var(--renova-green))] rounded-lg bg-emerald-500/10 p-6">
          <h2 className="font-heading font-bold text-lg flex items-center gap-2 mb-4">
            <Award className="w-6 h-6 text-[hsl(var(--renova-green))]" />
            Recommandation — Cliquez pour voir le détail
            {multiN > 1 && <span className="text-xs font-normal bg-emerald-200 text-emerald-800 px-2 py-0.5 rounded-full ml-2">{multiN} logements — Totaux affichés</span>}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {recommendation.map((opt, i) => (
              <button key={opt.type} onClick={() => setSelectedDetail(opt.type)}
                className={cn("p-5 rounded-lg border-2 transition-all text-left",
                  activeDetail === opt.type
                    ? "border-[hsl(var(--renova-green))] bg-card shadow-lg ring-2 ring-emerald-300"
                    : "border-border bg-card hover:border-emerald-500/40 hover:shadow-md"
                )} data-testid={`reco-${opt.type}`}>
                {i === 0 && <p className="text-xs font-bold text-emerald-600 uppercase mb-2">Recommandé</p>}
                <p className="font-semibold">{opt.label}</p>
                <div className="mt-3 space-y-1 text-sm">
                  {!isRegieUser && <div className="flex justify-between"><span className="text-muted-foreground">CA Total</span><span className="font-semibold text-primary">{formatCurrency(opt.ca)}</span></div>}
                  {showFinancials && <div className="flex justify-between"><span className="text-muted-foreground">Marge Totale</span><span className={cn("font-bold", opt.marge >= 0 ? "text-emerald-600" : "text-red-600")}>{formatCurrency(opt.marge)}</span></div>}
                  {showFinancials && isAdminFull && multiN > 1 && (
                    <div className="flex justify-between text-[10px] pt-1 border-t border-border mt-1"><span className="text-muted-foreground">Par logement</span><span>{formatCurrency(opt.margeUnit)}</span></div>
                  )}
                </div>
                {activeDetail === opt.type && <div className="w-full h-1 bg-[hsl(var(--renova-green))] rounded-full mt-3" />}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Éligibilité */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <EligCard title="Programme 174" eligible={elig174.eligible} reason={elig174.reason} color="emerald" />
        <EligCard title="Programme MPR" eligible={eligMPR.eligible} reason={eligMPR.reason} color="blue" />
        <EligCard title="Op. Cumulée" eligible={eligCumule.eligible} reason={eligCumule.reason} color="violet" />
      </div>

      {/* Ordre montage */}
      {eligCumule.eligible && eligCumule.order && (
        <div className="border-2 border-violet-300 rounded-lg bg-violet-500/10 p-4 flex items-center gap-3">
          <ArrowRight className="w-5 h-5 text-violet-600 shrink-0" />
          <div className="text-sm">
            <span className="font-semibold text-violet-700">Ordre du montage Op. Cumulée :</span>
            <span className="ml-2 font-bold text-violet-900">
              {eligCumule.order.map((p, i) => (
                <span key={p}>
                  {i > 0 && <span className="text-violet-400 mx-2">→</span>}
                  <span className={p === "174" ? "text-emerald-700" : "text-blue-700"}>{p === "174" ? "Programme 174" : "Programme MPR"}</span>
                </span>
              ))}
            </span>
          </div>
        </div>
      )}

      {/* Détail 174 */}
      {(activeDetail === "174" || isCum) && elig174.eligible && display174.length > 0 && (
        <div className="border border-emerald-500/30 rounded-lg bg-card p-6 space-y-4">
          <h3 className="font-semibold text-lg flex items-center gap-2 text-emerald-700">
            <TrendingUp className="w-5 h-5" /> Programme 174 — Scénarios
            {isCum && <span className="text-xs text-violet-600 font-normal ml-2">(régie cumulée : {regies.r174Cum}€)</span>}
          </h3>
          <div className="space-y-3">
            {display174.map(({ scenario, result }, i) => {
              const isActive = active174 && active174.scenario === scenario;
              const isBest = best174 && best174.scenario === scenario;
              return (
                <button key={i} onClick={() => {
                  // Fix: map display174 index to results174 index to avoid mismatch in cumulé mode
                  const origIdx = results174.findIndex(r => r.scenario === scenario);
                  setSelected174Idx(origIdx >= 0 ? origIdx : i);
                }}
                  className={cn("w-full p-4 rounded-lg border text-left transition-all",
                    isActive ? "border-emerald-400 bg-emerald-500/10 shadow-md" : "border-border hover:border-emerald-500/40"
                  )} data-testid={`scenario-174-${i}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      {isBest && <span className="text-xs font-bold text-emerald-600 uppercase">Recommandé</span>}
                      <p className="font-medium text-sm">{scenario.label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {scenario.sauts} sauts — {scenario.gestes.map(g => GESTES_ISO_LABELS[g] || g).join(", ")}
                        {scenario.equipements.length > 0 && ` + ${scenario.equipements.map(e => COUT_UNITAIRE[e]?.label || e).join(", ")}`}
                      </p>
                    </div>
                    <div className="flex gap-6 text-sm">
                      {!isRegieUser && (
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">CA TTC{multiN > 1 ? ` (x${multiN})` : ""}</p>
                        <p className="font-semibold">{formatCurrency(result.caTTC * multiN)}</p>
                      </div>
                      )}
                      {showFinancials && (
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">Coût{multiN > 1 ? ` (x${multiN})` : ""}</p>
                        <p className="font-semibold text-red-600">-{formatCurrency(result.coutTravaux * multiN)}</p>
                      </div>
                      )}
                      {showFinancials && (
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">Marge{multiN > 1 ? ` (x${multiN})` : ""}</p>
                        {showFinancials && <p className={cn("font-bold", result.marge >= 0 ? "text-emerald-600" : "text-red-600")}>{formatCurrency(result.marge * multiN)}</p>}
                      </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Détail coûts du scénario actif — Renova only */}
          {showFinancials && active174 && (() => {
            // En mode cumulé, utiliser le scénario corrigé (respectant maxSauts)
            const detail174 = isCum && active174Cumule ? active174Cumule : active174;
            return (
            <div className="border border-emerald-500/20 rounded-lg bg-emerald-500/10 p-5 space-y-3">
              <h4 className="font-semibold text-sm text-emerald-800 flex items-center gap-2">
                <Hammer className="w-4 h-4" /> Détail coûts — {detail174.scenario.label}
              </h4>
              <div className="space-y-1">
                {detail174.scenario.gestes.map(g => {
                  const qty = autoQtes[g];
                  const unit = (couts[g]?.materiel || 0) + (couts[g]?.pose || 0);
                  return (
                    <div key={g} className="flex justify-between text-sm py-1.5 border-b border-emerald-500/30/50">
                      <span>{GESTES_ISO_LABELS[g] || COUT_UNITAIRE[g]?.label}</span>
                      <span className="text-muted-foreground">{qty} {COUT_UNITAIRE[g]?.unite} x {unit} €</span>
                      <span className="font-medium w-24 text-right">{(qty * unit).toLocaleString('fr-FR')} €</span>
                    </div>
                  );
                })}
                {detail174.scenario.equipements.map(e => {
                  const qty = autoQtes[e];
                  const unit = (couts[e]?.materiel || 0) + (couts[e]?.pose || 0);
                  return (
                    <div key={e} className="flex justify-between text-sm py-1.5 border-b border-emerald-500/30/50">
                      <span>{COUT_UNITAIRE[e]?.label}</span>
                      <span className="text-muted-foreground">{qty} {COUT_UNITAIRE[e]?.unite} x {unit} €</span>
                      <span className="font-medium w-24 text-right">{(qty * unit).toLocaleString('fr-FR')} €</span>
                    </div>
                  );
                })}
                <div className="flex justify-between text-sm py-1.5 border-b border-emerald-500/30/50">
                  <span>Coûts fixes</span>
                  <span className="text-muted-foreground">1 x {(couts.coutsFixes?.materiel || 0) + (couts.coutsFixes?.pose || 0)} €</span>
                  <span className="font-medium w-24 text-right">{((couts.coutsFixes?.materiel || 0) + (couts.coutsFixes?.pose || 0)).toLocaleString('fr-FR')} €</span>
                </div>
                <div className="flex justify-between text-sm py-1.5 border-b border-emerald-500/30/50">
                  <span>Régie {isCum ? "(cumulée)" : ""}</span>
                  <span></span>
                  <span className="font-medium w-24 text-right">{(isCum ? regies.r174Cum : regies.r174).toLocaleString('fr-FR')} €</span>
                </div>
              </div>
              <div className="space-y-2 pt-3 border-t border-emerald-500/40">
                {detail174.result.isJaune && (
                  <div className="flex justify-between text-sm">
                    <span className="text-yellow-700">CA TTC brut</span>
                    <span className="font-medium text-yellow-700">{formatCurrency(detail174.result.caTTCBrut)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span>CA TTC</span>
                  <span className="font-semibold text-primary">{formatCurrency(detail174.result.caTTC)}</span>
                </div>
                {showFinancials && (
                <div className={cn("flex justify-between text-lg font-bold pt-2 border-t border-emerald-500/40")}>
                  <span>Marge 174</span>
                  <span className={detail174.result.marge >= 0 ? "text-emerald-600" : "text-red-600"}>
                    {formatCurrency(detail174.result.marge)}
                  </span>
                </div>
                )}
              </div>
            </div>
            );
          })()}

          {/* Per-logement detail toggle and display */}
          {multiLogement && multiN > 1 && active174 && (
            <div className="space-y-3">
              <button onClick={() => setShowPerLogement(p => !p)}
                className="flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:text-emerald-900 transition-colors"
                data-testid="toggle-per-logement">
                <Layers className="w-4 h-4" />
                {showPerLogement ? "Masquer" : "Afficher"} le détail par logement ({nbLogements})
                <ChevronRight className={cn("w-4 h-4 transition-transform", showPerLogement && "rotate-90")} />
              </button>
              {showPerLogement && perLogementResults.length > 0 && (
                <div className="space-y-2">
                  {perLogementResults.map(({ index, surface: logSurface, autoQtes: logAutoQtes, result: logResult }) => {
                    const detail174 = isCum && active174Cumule ? active174Cumule : active174;
                    return (
                      <div key={index} className="border border-emerald-500/30 rounded-lg p-4 bg-card/50">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-emerald-800">Logement {index + 1} — {logSurface} m²</span>
                          <span className={cn("text-sm font-bold", logResult.marge >= 0 ? "text-emerald-600" : "text-red-600")}>
                            {showFinancials && `Marge: ${formatCurrency(logResult.marge)}`}
                          </span>
                        </div>
                        <div className="space-y-0.5 text-xs">
                          {detail174.scenario.gestes.map(g => {
                            const qty = logAutoQtes[g] || 0;
                            const unit = (couts[g]?.materiel || 0) + (couts[g]?.pose || 0);
                            return (
                              <div key={g} className="flex justify-between py-0.5">
                                <span>{GESTES_ISO_LABELS[g] || COUT_UNITAIRE[g]?.label}</span>
                                <span className="text-muted-foreground">{qty} {COUT_UNITAIRE[g]?.unite} x {unit} €</span>
                                <span className="font-medium w-20 text-right">{(qty * unit).toLocaleString('fr-FR')} €</span>
                              </div>
                            );
                          })}
                          {detail174.scenario.equipements.map(e => {
                            const qty = logAutoQtes[e] || 0;
                            const unit = (couts[e]?.materiel || 0) + (couts[e]?.pose || 0);
                            return (
                              <div key={e} className="flex justify-between py-0.5">
                                <span>{COUT_UNITAIRE[e]?.label}</span>
                                <span className="text-muted-foreground">{qty} {COUT_UNITAIRE[e]?.unite} x {unit} €</span>
                                <span className="font-medium w-20 text-right">{(qty * unit).toLocaleString('fr-FR')} €</span>
                              </div>
                            );
                          })}
                          <div className="flex justify-between pt-1 border-t border-emerald-500/30 mt-1">
                            {!isRegieUser && <span className="font-semibold">CA TTC</span>}
                            {!isRegieUser && <span className="font-semibold">{formatCurrency(logResult.caTTC)}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {/* Totals summary */}
                  <div className="border-2 border-emerald-500/40 rounded-lg p-4 bg-emerald-500/10">
                    <div className="flex justify-between text-sm font-bold">
                      <span className="text-emerald-800">Total {nbLogements} logements</span>
                      <div className="flex gap-6">
                        {!isRegieUser && <span>CA: {formatCurrency(perLogementResults.reduce((s, r) => s + r.result.caTTC, 0))}</span>}
                        <span className={perLogementResults.reduce((s, r) => s + r.result.marge, 0) >= 0 ? "text-emerald-600" : "text-red-600"}>
                          {showFinancials && `Marge: ${formatCurrency(perLogementResults.reduce((s, r) => s + r.result.marge, 0))}`}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Détail MPR — 3 scénarios */}
      {(activeDetail === "mpr" || isCum) && eligMPR.eligible && displayMPR.length > 0 && (
        <div className="border border-blue-500/30 rounded-lg bg-card p-6 space-y-4">
          <h3 className="font-semibold text-lg flex items-center gap-2 text-blue-700">
            <Euro className="w-5 h-5" /> Programme MPR — Scénarios
            {isCum && <span className="text-xs text-violet-600 font-normal ml-2">(régie cumulée : {regies.rMPRCum}€)</span>}
          </h3>
          {periode !== "from_jan_2026" && (
            <div className="px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs text-amber-700" data-testid="periode-mpr-info">
              Avant jan. 2026 : marge fixe ({precarite === "bleu" ? "Bleu = 6 000€" : "Jaune = 3 300€"}). Le CEE est ajusté pour atteindre cette marge.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {displayMPR.map((r) => {
              const isBest = displayBestMPR && r.type === displayBestMPR.type;
              const isActive = activeMPR && r.type === activeMPR.type;
              return (
                <button key={r.type} onClick={() => setSelectedMPRType(r.type)}
                  className={cn("p-4 rounded-lg border-2 text-left transition-all",
                    isActive ? "border-blue-400 bg-blue-500/10 shadow-md" : "border-border hover:border-blue-500/40"
                  )} data-testid={`mpr-scenario-${r.type.replace(/[/\s+]/g, '-')}`}>
                  {isBest && <p className="text-xs font-bold text-blue-600 uppercase mb-1">Recommandé</p>}
                  <p className="font-semibold text-sm">{r.type}</p>
                  <div className="mt-2 space-y-1 text-xs">
                    {!isRegieUser && <div className="flex justify-between"><span className="text-muted-foreground">Aide MPR</span><span className="font-medium">{formatCurrency(r.result.mpr)}</span></div>}
                    {!isRegieUser && <div className="flex justify-between"><span className="text-muted-foreground">CEE</span><span className="font-medium">{formatCurrency(r.result.cee)}</span></div>}
                    {!isRegieUser && <div className="flex justify-between"><span className="text-muted-foreground">Total aides</span><span className="font-semibold">{formatCurrency(r.result.totalAides)}</span></div>}
                    {showFinancials && <div className="flex justify-between pt-1 border-t border-blue-500/30">
                      <span className="text-muted-foreground">Marge</span>
                      {showFinancials && <span className={cn("font-bold", r.result.marge >= 0 ? "text-emerald-600" : "text-red-600")}>{formatCurrency(r.result.marge)}</span>}
                    </div>}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Détail du scénario MPR actif — Renova only */}
          {showFinancials && activeMPR && (
            <div className="border border-blue-100 rounded-lg bg-blue-500/10 p-5 space-y-2 text-sm">
              <h4 className="font-semibold text-sm text-blue-800">Détail — {activeMPR.type}</h4>
              <div className="space-y-1">
                <div className="flex justify-between py-1 border-b border-blue-500/30/50"><span>Zone</span><span className="font-medium">{zone}</span></div>
                <div className="flex justify-between py-1 border-b border-blue-500/30/50"><span>Surface</span><span className="font-medium">{surface} m² ({activeMPR.result.surfKey})</span></div>
                <div className="flex justify-between py-1 border-b border-blue-500/30/50"><span>Précarité</span><span className="font-medium">{precarite === "bleu" ? "Bleu (Très modeste)" : "Jaune (Modeste)"}</span></div>
                <div className="flex justify-between py-1 border-b border-blue-500/30/50"><span>kWhc</span><span className="font-medium">{activeMPR.result.kwhc?.toLocaleString('fr-FR')}</span></div>
                <div className="flex justify-between py-1 border-b border-blue-500/30/50"><span>CEE base ({activeMPR.result.kwhc?.toLocaleString('fr-FR')} x {activeMPR.result.prixRachat} x 1.055)</span><span className="font-medium">{formatCurrency(activeMPR.result.ceeBase)}</span></div>
                {activeMPR.result.complementCEE > 0 && (
                  <div className="flex justify-between py-1 border-b border-amber-500/30 bg-amber-500/10 px-2 rounded text-amber-700">
                    <span>Complément CEE (marge fixe avant jan. 2026)</span>
                    <span className="font-medium">+{formatCurrency(activeMPR.result.complementCEE)}</span>
                  </div>
                )}
                <div className="flex justify-between py-1 border-b border-blue-500/30/50"><span>CEE total</span><span className="font-semibold">{formatCurrency(activeMPR.result.cee)}</span></div>
                <div className="flex justify-between py-1 border-b border-blue-500/30/50"><span>Aide MPR ({activeMPR.type})</span><span className="font-semibold">{formatCurrency(activeMPR.result.mpr)}</span></div>
                <div className="flex justify-between py-1 font-semibold"><span>Total aides</span><span className="text-primary">{formatCurrency(activeMPR.result.totalAides)}</span></div>
              </div>
              {/* Charges MPR */}
              {(() => {
                const ch = CHARGES_MPR_DEFAUT[activeMPR.type] || CHARGES_MPR_DEFAUT["PAC/SSC"];
                const regie = isCum ? regies.rMPRCum : regies.rMPR;
                const totalCharges = ch.marchandise + ch.sousTraitant + ch.admin + regie;
                return (
                  <div className="pt-2 border-t border-blue-500/30 space-y-1">
                    <p className="font-semibold text-blue-800 text-xs">Charges — {activeMPR.type}</p>
                    <div className="flex justify-between py-1 border-b border-blue-500/30/50"><span>Marchandise</span><span className="font-medium text-red-600">-{formatCurrency(ch.marchandise)}</span></div>
                    <div className="flex justify-between py-1 border-b border-blue-500/30/50"><span>Sous-traitant</span><span className="font-medium text-red-600">-{formatCurrency(ch.sousTraitant)}</span></div>
                    <div className="flex justify-between py-1 border-b border-blue-500/30/50"><span>Admin</span><span className="font-medium text-red-600">-{formatCurrency(ch.admin)}</span></div>
                    <div className="flex justify-between py-1 border-b border-blue-500/30/50"><span>Régie MPR {isCum ? "(cumulée)" : ""}</span><span className="font-medium text-red-600">-{formatCurrency(regie)}</span></div>
                    <div className="flex justify-between py-1 font-semibold"><span>Total charges</span><span className="text-red-600">-{formatCurrency(totalCharges)}</span></div>
                  </div>
                );
              })()}
              {showFinancials && (
              <div className={cn("flex justify-between text-lg font-bold pt-2 border-t border-blue-500/40")}>
                <span>Marge MPR</span>
                {showFinancials && <span className={activeMPR.result.marge >= 0 ? "text-emerald-600" : "text-red-600"}>{formatCurrency(activeMPR.result.marge)}</span>}
              </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Per-logement MPR detail */}
      {(activeDetail === "mpr" || isCum) && multiLogement && multiN > 1 && perLogementMPRResults.length > 0 && (
        <div className="border border-blue-500/30 rounded-lg bg-card p-6 space-y-3">
          <h4 className="font-semibold text-sm text-blue-700 flex items-center gap-2">
            <Layers className="w-4 h-4" /> Détail MPR par logement ({nbLogements} logements)
          </h4>
          <div className="space-y-2">
            {perLogementMPRResults.map(({ index, surface: logSurface, type: mprType, result: logResult }) => (
              <div key={index} className="border border-blue-500/30 rounded-lg p-4 bg-card/50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-blue-800">Logement {index + 1} — {logSurface} m² ({mprType})</span>
                  {showFinancials && (
                    <span className={cn("text-sm font-bold", logResult.marge >= 0 ? "text-emerald-600" : "text-red-600")}>
                      {showFinancials && `Marge: ${formatCurrency(logResult.marge)}`}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  {!isRegieUser && <div><span className="text-muted-foreground">Aide MPR:</span> <span className="font-medium">{formatCurrency(logResult.mpr)}</span></div>}
                  <div><span className="text-muted-foreground">CEE:</span> <span className="font-medium">{formatCurrency(logResult.cee)}</span></div>
                  <div><span className="text-muted-foreground">Total:</span> <span className="font-semibold">{formatCurrency(logResult.totalAides)}</span></div>
                </div>
              </div>
            ))}
            {/* Totals */}
            <div className="border-2 border-blue-500/40 rounded-lg p-4 bg-blue-500/10">
              <div className="flex justify-between text-sm font-bold">
                <span className="text-blue-800">Total {nbLogements} logements (MPR)</span>
                <div className="flex gap-6">
                  <span>Aides: {formatCurrency(perLogementMPRResults.reduce((s, r) => s + r.result.totalAides, 0))}</span>
                  {showFinancials && (
                    <span className={perLogementMPRResults.reduce((s, r) => s + r.result.marge, 0) >= 0 ? "text-emerald-600" : "text-red-600"}>
                      {showFinancials && `Marge: ${formatCurrency(perLogementMPRResults.reduce((s, r) => s + r.result.marge, 0))}`}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Total Combiné pour Op. Cumulée */}
      {showFinancials && isCum && active174Cumule && activeMPRCumule && (() => {
        const usePerLog = multiLogement && surfaceMode === "detail" && multiN > 1;
        const ca174Total = usePerLog && perLogementResults.length > 0 ? perLogementResults.reduce((s, r) => s + r.result.caTTC, 0) : active174Cumule.result.caTTC * (multiN || 1);
        const caMPRTotal = usePerLog && perLogementMPRResults.length > 0 ? perLogementMPRResults.reduce((s, r) => s + r.result.totalAides, 0) : activeMPRCumule.result.totalAides * (multiN || 1);
        const marge174Total = usePerLog && perLogementResults.length > 0 ? perLogementResults.reduce((s, r) => s + r.result.marge, 0) : active174Cumule.result.marge * (multiN || 1);
        const margeMPRTotal = usePerLog && perLogementMPRResults.length > 0 ? perLogementMPRResults.reduce((s, r) => s + r.result.marge, 0) : activeMPRCumule.result.marge * (multiN || 1);
        return (
        <div className="border-2 border-violet-300 rounded-lg bg-violet-500/10 p-6 space-y-3">
          <h3 className="font-semibold text-lg flex items-center gap-2 text-violet-700">
            <Layers className="w-5 h-5" /> Total Op. Cumulée (174 + MPR){multiN > 1 && <span className="text-xs font-normal ml-2">({multiN} logements)</span>}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">CA 174</p>
              <p className="font-semibold">{formatCurrency(ca174Total)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">CA MPR</p>
              <p className="font-semibold">{formatCurrency(caMPRTotal)}</p>
            </div>
            {showFinancials && (
            <>
            <div>
              <p className="text-muted-foreground">Marge 174</p>
              <p className={cn("font-semibold", marge174Total >= 0 ? "text-emerald-600" : "text-red-600")}>{formatCurrency(marge174Total)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Marge MPR</p>
              <p className={cn("font-semibold", margeMPRTotal >= 0 ? "text-emerald-600" : "text-red-600")}>{formatCurrency(margeMPRTotal)}</p>
            </div>
            </>
            )}
          </div>
          <div className="flex justify-between items-center pt-3 border-t border-violet-300">
            <div>
              <p className="text-sm text-muted-foreground">CA total combiné</p>
              <p className="font-bold text-lg text-primary">{formatCurrency(ca174Total + caMPRTotal)}</p>
            </div>
            {showFinancials && (
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Marge totale combinée</p>
              <p className={cn("font-bold text-2xl", (marge174Total + margeMPRTotal) >= 0 ? "text-emerald-600" : "text-red-600")}>
                {formatCurrency(marge174Total + margeMPRTotal)}
              </p>
            </div>
            )}
          </div>
        </div>
        );
      })()}

      {/* ═══ FRISE CHRONOLOGIQUE ═══ */}
      {(elig174.eligible || eligMPR.eligible) && (() => {
        const { timelines, cashEvents, totalDays } = computeAllTimelines(activeDetail);
        if (timelines.length === 0) return null;

        return (
          <>
            {renderTimeline(timelines, totalDays, null)}

            {/* Frise trésorerie — Renova only */}
            {showFinancials && cashEvents.length > 0 && (
              <div className="border border-border rounded-xl bg-card p-6 space-y-4" data-testid="frise-tresorerie">
                <h3 className="font-semibold text-base flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-[hsl(var(--renova-green))]" /> Flux de trésorerie
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="border-b-2 border-border">
                        <th className="text-left py-2.5 px-3 font-semibold text-muted-foreground">Jour</th>
                        <th className="text-left py-2.5 px-3 font-semibold text-muted-foreground">Date</th>
                        <th className="text-left py-2.5 px-3 font-semibold text-muted-foreground">Programme</th>
                        <th className="text-left py-2.5 px-3 font-semibold text-muted-foreground">Événement</th>
                        <th className="text-right py-2.5 px-3 font-semibold text-muted-foreground">Montant</th>
                        <th className="text-right py-2.5 px-3 font-semibold text-muted-foreground">Solde cumulé</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cashEvents.map((ev, i) => (
                        <tr key={i} className={cn("border-b border-border/40 transition-colors", ev.amount >= 0 ? "bg-emerald-500/10/30" : "bg-red-500/10/30")}>
                          <td className="py-2.5 px-3 font-bold">J{ev.day}</td>
                          <td className="py-2.5 px-3 text-muted-foreground">{ev.dateStr}</td>
                          <td className="py-2.5 px-3">
                            <span className={cn("px-2 py-0.5 rounded text-[10px] font-bold uppercase", ev.prog === "MPR" ? "bg-blue-500/20 text-blue-600 dark:text-blue-400" : "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400")}>{ev.prog}</span>
                          </td>
                          <td className="py-2.5 px-3 font-medium">{ev.label}</td>
                          <td className={cn("py-2.5 px-3 text-right font-bold", ev.amount >= 0 ? "text-emerald-600" : "text-red-600")}>
                            {ev.amount >= 0 ? "+" : ""}{formatCurrency(ev.amount)}
                          </td>
                          <td className={cn("py-2.5 px-3 text-right font-bold", ev.balance >= 0 ? "text-emerald-700" : "text-red-700")}>
                            {formatCurrency(ev.balance)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-border bg-muted/30">
                        <td colSpan={4} className="py-3 px-3 font-bold text-sm">Solde final</td>
                        <td className="py-3 px-3"></td>
                        <td className={cn("py-3 px-3 text-right font-bold text-base", cashEvents[cashEvents.length - 1]?.balance >= 0 ? "text-emerald-700" : "text-red-700")}>
                          {formatCurrency(cashEvents[cashEvents.length - 1]?.balance || 0)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <div className="flex items-center gap-4 text-xs text-muted-foreground pt-1">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Encaissement</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> Décaissement</span>
                  <span>Mode : {regieMode === "regie" ? "Régie (paiement au moment du Paiement programme)" : "Lead (paiement à J60)"}</span>
                </div>
              </div>
            )}
          </>
        );
      })()}

      {/* Actions: Créer dossier + Export PDF */}
      {(elig174.eligible || eligMPR.eligible) && (
        <div className="flex gap-3 justify-center flex-wrap pt-2">
          {eligCumule.eligible && (
            <Button onClick={() => openBenefPopup('cumule')} disabled={adminSaving} className="rounded-lg bg-violet-600 hover:bg-violet-700" data-testid="btn-creer-dossier-cumule">
              <FilePlus className="w-4 h-4 mr-2" /> Créer un dossier Op. Cumulée
            </Button>
          )}
          {elig174.eligible && (
            <Button onClick={() => openBenefPopup('174')} disabled={adminSaving} className="rounded-lg bg-emerald-600 hover:bg-emerald-700" data-testid="btn-creer-dossier-174">
              <FilePlus className="w-4 h-4 mr-2" /> Créer un dossier 174
            </Button>
          )}
          {eligMPR.eligible && (
            <Button onClick={() => openBenefPopup('mpr')} disabled={adminSaving} className="rounded-lg bg-blue-600 hover:bg-blue-700" data-testid="btn-creer-dossier-mpr">
              <FilePlus className="w-4 h-4 mr-2" /> Créer un dossier MPR
            </Button>
          )}
          {showExport && (
          <Button variant="outline" className="rounded-lg" data-testid="btn-export-pdf" onClick={() => {
            const doc = new jsPDF();
            const fc = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v || 0);
            let y = 20;
            doc.setFontSize(18);
            doc.text("Simulation Travaux - Résultats", 14, y); y += 12;
            doc.setFontSize(10);
            doc.text(`Zone: ${zone} | Surface: ${surface} m² | Précarité: ${PRECARITE_COLORS[precarite]?.label || precarite}`, 14, y); y += 6;
            doc.text(`Mode coût: ${regieMode === "lead" ? "Lead" : "Régie"} | Période: ${periode === "from_jan_2026" ? "À partir de jan. 2026" : "Avant jan. 2026"}`, 14, y); y += 10;
            doc.setFontSize(14);
            doc.text("Recommandation", 14, y); y += 8;
            doc.setFontSize(10);
            recommendation.forEach((opt, i) => {
              doc.text(`${i === 0 ? "* " : "  "}${opt.label} — CA: ${fc(opt.ca)} — Marge: ${fc(opt.marge)}${i === 0 ? " (le plus rentable)" : ""}`, 14, y); y += 6;
            });
            y += 6;
            if (active174) {
              doc.setFontSize(12);
              doc.text("Programme 174", 14, y); y += 7;
              doc.setFontSize(10);
              doc.text(`Scénario: ${active174.scenario.label} (${active174.scenario.sauts} sauts)`, 14, y); y += 6;
              doc.text(`CA TTC: ${fc(active174.result.caTTC)} | Coût: ${fc(active174.result.coutTravaux)} | Marge: ${fc(active174.result.marge)}`, 14, y); y += 6;
              doc.text(`Régie 174: ${regies.r174} € | Valorisation: ${valorisation} €/MWh`, 14, y); y += 10;
            }
            if (activeMPR) {
              doc.setFontSize(12);
              doc.text("Programme MPR", 14, y); y += 7;
              doc.setFontSize(10);
              doc.text(`Type: ${activeMPR.type}`, 14, y); y += 6;
              doc.text(`Aide MPR: ${fc(activeMPR.result.mpr)} | CEE: ${fc(activeMPR.result.cee)} | Total aides: ${fc(activeMPR.result.totalAides)}`, 14, y); y += 6;
              doc.text(`Marge MPR: ${fc(activeMPR.result.marge)} | Régie MPR: ${regies.rMPR} €`, 14, y); y += 6;
              if (activeMPR.result.complementCEE > 0) {
                doc.text(`Complément CEE (marge fixe): +${fc(activeMPR.result.complementCEE)}`, 14, y); y += 6;
              }
              y += 4;
              doc.setFontSize(10);
              doc.text("Tous les scénarios MPR:", 14, y); y += 6;
              allMPRResults.forEach(r => {
                doc.text(`  ${r.type}: Marge ${fc(r.result.marge)} | Aides ${fc(r.result.totalAides)}${r.type === bestMPR?.type ? " (recommandé)" : ""}`, 14, y); y += 5;
              });
              y += 6;
            }
            if (eligCumule.eligible && active174Cumule && activeMPRCumule) {
              doc.setFontSize(12);
              doc.text("Op. Cumulée", 14, y); y += 7;
              doc.setFontSize(10);
              doc.text(`Ordre: ${eligCumule.order.join(" puis ")}`, 14, y); y += 6;
              doc.text(`Marge 174 (cum.): ${fc(active174Cumule.result.marge)} | Marge MPR (cum.): ${fc(activeMPRCumule.result.marge)}`, 14, y); y += 6;
              doc.text(`Marge totale: ${fc(active174Cumule.result.marge + activeMPRCumule.result.marge)}`, 14, y); y += 6;
            }
            doc.save("simulation-travaux.pdf");
          }}>
            <Euro className="w-4 h-4 mr-2" /> Exporter PDF
          </Button>
          )}
        </div>
      )}

      {/* Non éligible */}
      {!elig174.eligible && !eligMPR.eligible && (
        <div className="border-2 border-red-300 rounded-lg bg-red-500/10 p-8 text-center">
          <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <h3 className="font-bold text-lg text-red-700">Aucun programme éligible</h3>
          <p className="text-red-600 text-sm mt-2">Ce logement ne correspond aux critères d'aucun programme avec les paramètres actuels.</p>
        </div>
      )}

      {/* ═══ BENEFICIARY INFO POPUP ═══ */}
      <Dialog open={showBenefPopup} onOpenChange={setShowBenefPopup}>
        <DialogContent className="rounded-xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="beneficiary-popup">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <User className="w-5 h-5 text-indigo-500" />
              Informations du bénéficiaire
            </DialogTitle>
            <DialogDescription>
              Renseignez les informations du bénéficiaire avant de créer le dossier {selectedProgramme === "cumule" ? "Op. Cumulée" : selectedProgramme === "mpr" ? "MPR" : "174"}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Common fields */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Nom du bénéficiaire *</Label>
                <Input value={benefInfo.client_name} onChange={(e) => setBenefInfo(p => ({...p, client_name: e.target.value}))}
                  placeholder="Nom complet" className="rounded-lg mt-1" data-testid="benef-name" />
              </div>
              <div>
                <Label className="text-xs">Téléphone</Label>
                <Input value={benefInfo.client_phone} onChange={(e) => setBenefInfo(p => ({...p, client_phone: e.target.value}))}
                  placeholder="06 XX XX XX XX" className="rounded-lg mt-1" data-testid="benef-phone" />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input value={benefInfo.client_email} onChange={(e) => setBenefInfo(p => ({...p, client_email: e.target.value}))}
                  type="email" placeholder="email@exemple.fr" className="rounded-lg mt-1" data-testid="benef-email" />
              </div>
              <div>
                <Label className="text-xs">Code postal</Label>
                <Input value={benefInfo.code_postal} onChange={(e) => setBenefInfo(p => ({...p, code_postal: e.target.value}))}
                  placeholder="75001" className="rounded-lg mt-1" data-testid="benef-cp" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Adresse du logement</Label>
                <Input value={benefInfo.adresse_logement} onChange={(e) => setBenefInfo(p => ({...p, adresse_logement: e.target.value}))}
                  placeholder="Adresse complète" className="rounded-lg mt-1" data-testid="benef-adresse" />
              </div>
              <div>
                <Label className="text-xs">Profil client</Label>
                <Select value={benefInfo.profil_client} onValueChange={(v) => setBenefInfo(p => ({...p, profil_client: v}))}>
                  <SelectTrigger className="rounded-lg mt-1" data-testid="benef-profil">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="proprietaire_occupant">Propriétaire occupant</SelectItem>
                    <SelectItem value="bailleur">Bailleur</SelectItem>
                    <SelectItem value="sci">SCI</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Zone climatique</Label>
                <Select value={benefInfo.zone} onValueChange={(v) => setBenefInfo(p => ({...p, zone: v}))}>
                  <SelectTrigger className="rounded-lg mt-1" data-testid="benef-zone">
                    <SelectValue placeholder="Zone" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="H1">H1</SelectItem>
                    <SelectItem value="H2">H2</SelectItem>
                    <SelectItem value="H3">H3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Bailleur/SCI specific fields */}
            {(benefInfo.profil_client === "bailleur" || benefInfo.profil_client === "sci") && (
              <div className="border border-amber-500/30 bg-amber-500/10 rounded-lg p-3 space-y-3">
                <p className="text-xs font-semibold text-amber-700">Informations bailleur</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Organisme bailleur</Label>
                    <Input value={benefInfo.organisme_bailleur} onChange={(e) => setBenefInfo(p => ({...p, organisme_bailleur: e.target.value}))}
                      className="rounded-lg mt-1" data-testid="benef-organisme" />
                  </div>
                  <div>
                    <Label className="text-xs">Nom du représentant</Label>
                    <Input value={benefInfo.nom_representant} onChange={(e) => setBenefInfo(p => ({...p, nom_representant: e.target.value}))}
                      className="rounded-lg mt-1" data-testid="benef-representant" />
                  </div>
                  <div>
                    <Label className="text-xs">Tél. représentant</Label>
                    <Input value={benefInfo.tel_representant} onChange={(e) => setBenefInfo(p => ({...p, tel_representant: e.target.value}))}
                      className="rounded-lg mt-1" data-testid="benef-tel-rep" />
                  </div>
                  <div>
                    <Label className="text-xs">Email représentant</Label>
                    <Input value={benefInfo.email_representant} onChange={(e) => setBenefInfo(p => ({...p, email_representant: e.target.value}))}
                      className="rounded-lg mt-1" data-testid="benef-email-rep" />
                  </div>
                  <div>
                    <Label className="text-xs">Adresse bailleur</Label>
                    <Input value={benefInfo.adresse_bailleur} onChange={(e) => setBenefInfo(p => ({...p, adresse_bailleur: e.target.value}))}
                      className="rounded-lg mt-1" data-testid="benef-adresse-bailleur" />
                  </div>
                  <div>
                    <Label className="text-xs">Nom de la résidence</Label>
                    <Input value={benefInfo.nom_residence} onChange={(e) => setBenefInfo(p => ({...p, nom_residence: e.target.value}))}
                      className="rounded-lg mt-1" data-testid="benef-residence" />
                  </div>
                  <div>
                    <Label className="text-xs">Adresse résidence</Label>
                    <Input value={benefInfo.adresse_residence} onChange={(e) => setBenefInfo(p => ({...p, adresse_residence: e.target.value}))}
                      className="rounded-lg mt-1" data-testid="benef-adresse-res" />
                  </div>
                  <div>
                    <Label className="text-xs">Coordonnées gardien</Label>
                    <Input value={benefInfo.coordonnees_gardien} onChange={(e) => setBenefInfo(p => ({...p, coordonnees_gardien: e.target.value}))}
                      className="rounded-lg mt-1" data-testid="benef-gardien" />
                  </div>
                </div>
              </div>
            )}

            <div>
              <Label className="text-xs">Notes</Label>
              <Input value={benefInfo.notes} onChange={(e) => setBenefInfo(p => ({...p, notes: e.target.value}))}
                placeholder="Notes supplémentaires..." className="rounded-lg mt-1" data-testid="benef-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBenefPopup(false)} className="rounded-lg" data-testid="benef-cancel">
              Annuler
            </Button>
            <Button onClick={() => handleAdminCreateDossier(selectedProgramme)} disabled={adminSaving}
              className={cn("rounded-lg", selectedProgramme === "mpr" ? "bg-blue-600 hover:bg-blue-700" : selectedProgramme === "cumule" ? "bg-violet-600 hover:bg-violet-700" : "bg-emerald-600 hover:bg-emerald-700")}
              data-testid="benef-submit">
              <FilePlus className="w-4 h-4 mr-2" />
              {adminSaving ? "Création..." : "Créer le dossier"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
